import React, { useContext, useState, useEffect, useRef } from 'react';
import { Box, InputBase, IconButton, Paper, Typography, Avatar, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { styled, keyframes } from '@mui/material/styles';
import axios from 'axios';
import { PerformerContext } from '../contexts/PerformerContext';
import { useFapHouseSearch } from '../hooks/useFapHouseSearch';
import CamEmbedModal from './CamEmbedModal';

const slideDown = keyframes`from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}`;

const SearchWrap = styled(Box)({
  position: 'relative', width: '100%', maxWidth: 600,
});

const ResultsDropdown = styled(Paper)({
  position: 'absolute', top: '110%', left: 0, right: 0,
  zIndex: 1300, maxHeight: 400, overflowY: 'auto', borderRadius: 12,
  boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
  animation: `${slideDown} 0.15s ease`,
});

const SearchBar = () => {
  const { performers, handleFilterChange } = useContext(PerformerContext);
  const { searchFapHouse } = useFapHouseSearch();
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [embedP, setEmbedP]     = useState(null);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const wrapRef  = useRef(null);
  const debounceRef = useRef(null);

  // Sync search term to performer context so grid also filters
  // Also update FapHouse videos to match the search query
  useEffect(() => {
    handleFilterChange({ searchTerm: query.trim() });
    if (query.trim().length >= 2) {
      searchFapHouse(query.trim());
    }
  }, [query]); // eslint-disable-line

  useEffect(() => {
    if (!query.trim()) { setResults([]); setOpen(false); setSearching(false); return; }

    const q = query.toLowerCase();

    // Body-feature synonym map for local search (mirrors PerformerContext SEARCH_SYNONYMS)
    const SYNONYMS = {
      'big tits': 'bigboob', 'big boobs': 'bigboob', 'busty': 'bigboob', 'huge tits': 'bigboob',
      'big boob': 'bigboob', 'large tits': 'bigboob', 'big breasts': 'bigboob',
      'small tits': 'smallboob', 'small boobs': 'smallboob',
      'big ass': 'bigass', 'big butt': 'bigass', 'booty': 'bigass', 'bubble butt': 'bigass',
      'latina': 'hispanic', 'latin': 'hispanic', 'black': 'ebony', 'blonde': 'blond',
      'brunette': 'brown', 'redhead': 'red', 'ginger': 'red',
      'bbw': 'bbw', 'plus size': 'bbw', 'curvy': 'curvy',
      'milf': 'milf', 'mature': 'mature', 'teen': 'teen', 'young': 'teen',
      'tattoo': 'tattoo', 'tattooed': 'tattoo', 'piercing': 'piercing',
      'hairy': 'hairy', 'shaved': 'shaved',
    };
    const qMapped = SYNONYMS[q] || q;

    // 1. Instant local match from loaded performers
    const localMatches = performers.filter(p => {
      const fetishes = (Array.isArray(p.fetishes) ? p.fetishes : (p.fetishes || '').split(',')).map(f => f.toLowerCase().trim());
      return (
        p.displayName?.toLowerCase().includes(q) ||
        p.ethnicity?.toLowerCase().includes(q) ||
        p.ethnicity?.toLowerCase().includes(qMapped) ||
        p.hairColor?.toLowerCase().includes(q) ||
        p.hairColor?.toLowerCase().includes(qMapped) ||
        p.theme?.toLowerCase().includes(q) ||
        fetishes.some(f => f.includes(q) || f.includes(qMapped))
      );
    }).slice(0, 8);

    if (localMatches.length > 0) {
      setResults(localMatches);
      setOpen(true);
      setSearching(false);
    } else {
      // 2. Debounced API search — live feed first, then offline directory fallback
      setSearching(true);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/performers`, {
            params: { keyword: query.trim(), limit: 8, exact: false }
          });
          const apiResults = (Array.isArray(res.data) ? res.data : []).map(p => ({
            uniqueModelId: p['@attributes']?.Id,
            displayName: p['@attributes']?.Name,
            age: p['@attributes']?.Age,
            ethnicity: p['@attributes']?.Ethnicity,
            hairColor: p['@attributes']?.HairColor,
            profilePictureUrl: p?.Media?.Pic?.Thumb?.['@attributes']?.Src || '',
            embedUrl: `https://hybridclient.naiadsystems.com/purecam?performer=${p['@attributes']?.Name}&widescreen=true`,
            chatRoomUrl: `https://instacamgirlslive.com/cam/${p['@attributes']?.Name}/?AFNO=1-94033`,
            isLive: true,
          }));

          if (apiResults.length > 0) {
            setResults(apiResults);
            setOpen(true);
          } else {
            // Fallback: search offline performer directory
            const dirRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/performers-directory/search`, {
              params: { q: query.trim(), limit: 8 }
            });
            const dirResults = (Array.isArray(dirRes.data) ? dirRes.data : []).map(p => ({
              uniqueModelId: p.name,
              displayName: p.display_name || p.name,
              age: p.age,
              ethnicity: p.ethnicity,
              hairColor: p.hair_color,
              profilePictureUrl: p.profile_picture_url || '',
              chatRoomUrl: `/cam/${p.name}`,
              isLive: false,
            }));
            setResults(dirResults);
            setOpen(dirResults.length > 0);
          }
        } catch (e) {
          setResults([]);
          setOpen(false);
        } finally {
          setSearching(false);
        }
      }, 400);
    }
  }, [query, performers]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      {embedP && <CamEmbedModal performer={embedP} allPerformers={results} onClose={() => setEmbedP(null)} />}

      <SearchWrap ref={wrapRef}>
        <Paper elevation={0} sx={{
          display: 'flex', alignItems: 'center', border: '2px solid #eee',
          borderRadius: '50px', px: 2, py: 0.5, bgcolor: '#fafafa',
          '&:focus-within': { border: '2px solid #FF4196', bgcolor: '#fff' },
          transition: 'all 0.2s',
        }}>
          <SearchIcon sx={{ color: '#aaa', mr: 1, fontSize: 20 }} />
          <InputBase
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
            placeholder="Search models, categories, tags..."
            sx={{ flex: 1, fontSize: '0.9rem' }}
          />
          {query && (
            <IconButton size="small" onClick={() => { setQuery(''); setResults([]); setOpen(false); }}>
              <CloseIcon sx={{ fontSize: 16, color: '#aaa' }} />
            </IconButton>
          )}
        </Paper>

        {/* Loading spinner */}
        {searching && query.trim() && (
          <ResultsDropdown>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 2 }}>
              <CircularProgress size={16} sx={{ color: '#FF4196' }} />
              <Typography sx={{ fontSize: '0.85rem', color: '#999' }}>Searching live models...</Typography>
            </Box>
          </ResultsDropdown>
        )}

        {/* Results */}
        {!searching && open && results.length > 0 && (
          <ResultsDropdown>
            {results.map(p => (
              <Box key={p.uniqueModelId}
                onClick={() => {
                  setOpen(false);
                  setQuery('');
                  if (p.isLive === false) {
                    // Offline performer — go to profile page
                    window.location.href = `/cam/${p.uniqueModelId}`;
                  } else {
                    setEmbedP(p);
                  }
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
                  cursor: 'pointer', '&:hover': { bgcolor: '#fff0f7' },
                  borderBottom: '1px solid #f5f5f5' }}>
                <Avatar src={p.profilePictureUrl} sx={{ width: 40, height: 40 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.2 }}>
                    {p.displayName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#aaa' }}>
                    {[p.age && `${p.age} yrs`, p.ethnicity, p.hairColor].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                {p.isLive === false ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                    bgcolor: '#f3f3f3', borderRadius: '10px', px: 1, py: 0.3 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#aaa' }} />
                    <Typography sx={{ fontSize: '0.65rem', color: '#777', fontWeight: 700 }}>OFFLINE</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                    bgcolor: '#e8f5e9', borderRadius: '10px', px: 1, py: 0.3 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4CAF50' }} />
                    <Typography sx={{ fontSize: '0.65rem', color: '#2e7d32', fontWeight: 700 }}>LIVE</Typography>
                  </Box>
                )}
              </Box>
            ))}
          </ResultsDropdown>
        )}

        {/* No results */}
        {!searching && query.trim().length > 2 && open === false && results.length === 0 && (
          <ResultsDropdown>
            <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#999' }}>No models found for "{query}"</Typography>
            </Box>
          </ResultsDropdown>
        )}
      </SearchWrap>
    </>
  );
};

export default SearchBar;
